import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import MenuPaintedButton from '../components/MenuPaintedButton';
import { BATTLE_UI_TARGETING_BAR_BACKGROUND_PATH, MENU_PAINTED_SURFACE_FILTER, RESIGN_DIALOG_CONFIRM_BUTTON_PATH } from '../config/welcomeAssets';
import { resolvePublicAssetBackgroundUrl } from '../utils/resolvePublicAssetUrl';
import { BATTLE_TARGETING_Z_INDEX } from '../config/cursorConfig';
import './TargetingCancelDialog.css';
import getWeaponTargetingMessage from '../targeting/getWeaponTargetingMessage';
import { getStrategemTargetingPrompt } from '../targeting/getTargetingMessage';

const TARGETING_DIALOG_PORTAL_Z_INDEX = BATTLE_TARGETING_Z_INDEX.cancelDialog;

function isTutorialCancelTargetingDisabled() {
  return typeof document !== 'undefined' && document.body?.getAttribute('data-tutorial-overlay-disable-cancel-targeting') === 'true';
}

function validatePortalAnchor(portalAnchor) {
  if (!portalAnchor || typeof portalAnchor !== 'object') {
    throw new Error('TargetingCancelDialog.portalAnchor is required');
  }
  const { offsetX, offsetY, scale, baseWidth, baseHeight } = portalAnchor;
  if (typeof offsetX !== 'number') {
    throw new Error('TargetingCancelDialog.portalAnchor.offsetX must be a number');
  }
  if (typeof offsetY !== 'number') {
    throw new Error('TargetingCancelDialog.portalAnchor.offsetY must be a number');
  }
  if (typeof scale !== 'number') {
    throw new Error('TargetingCancelDialog.portalAnchor.scale must be a number');
  }
  if (typeof baseWidth !== 'number') {
    throw new Error('TargetingCancelDialog.portalAnchor.baseWidth must be a number');
  }
  if (typeof baseHeight !== 'number') {
    throw new Error('TargetingCancelDialog.portalAnchor.baseHeight must be a number');
  }
}

function buildPortalShellStyle(portalAnchor) {
  validatePortalAnchor(portalAnchor);
  const { offsetX, offsetY, scale, baseWidth, baseHeight } = portalAnchor;
  return {
    shell: {
      position: 'fixed',
      left: offsetX,
      top: offsetY,
      width: baseWidth * scale,
      height: baseHeight * scale,
      zIndex: TARGETING_DIALOG_PORTAL_Z_INDEX,
      pointerEvents: 'none',
    },
    stage: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: baseWidth,
      height: baseHeight,
      transform: `scale(${scale})`,
      transformOrigin: '0 0',
      pointerEvents: 'none',
    },
  };
}

function TargetingCancelButton({ onClick, children }) {
  return (
    <MenuPaintedButton
      type="button"
      className="targeting-cancel-button"
      imagePath={RESIGN_DIALOG_CONFIRM_BUTTON_PATH}
      onClick={onClick}
    >
      {children}
    </MenuPaintedButton>
  );
}

const TargetingCancelDialog = ({
  deployMode,
  strategemMode,
  weaponMode,
  weaponDeployMode,
  weaponDeployTargetInfo,
  droneMode,
  euEpZoneMode,
  euEpTargetMode,
  strategemTargetInfo,
  selectedDeployOrderId,
  deployTargetInfo,
  /** Localized card name from GET /order/:id?lang=… when available */
  deployOrderDisplayName,
  strategemOrderDisplayName,
  weaponOrderDisplayName,
  droneOrderDisplayName,
  onCancelDeploy,
  onCancelStrategem,
  onCancelWeapon,
  onCancelWeaponDeploy,
  onCancelDrone,
  onCancelEuEpZone,
  onCancelEuEpTarget,
  portalAnchor,
}) => {
  const { t } = useTranslation();
  
  // Don't render if no mode is active
  if (!deployMode && !strategemMode && !weaponMode && !weaponDeployMode && !droneMode && !euEpZoneMode && !euEpTargetMode) {
    return null;
  }

  // Unit 349 (Enhanced Scorpion) requires mandatory targeting - cannot cancel
  const isMandatoryTargeting = selectedDeployOrderId === 349;

  const dialog = (
    <div
      className="targeting-cancel-dialog"
      style={{
        '--targeting-cancel-dialog-background-image': resolvePublicAssetBackgroundUrl(BATTLE_UI_TARGETING_BAR_BACKGROUND_PATH),
        '--targeting-cancel-dialog-surface-filter': MENU_PAINTED_SURFACE_FILTER,
      }}
    >
      {deployMode && (
        <div className="targeting-message">
          {t('battleUI.selectTargetForDeployNamed', {
            name:
              (typeof deployOrderDisplayName === 'string' && deployOrderDisplayName.trim()) ||
              deployTargetInfo?.order?.name ||
              t('battleUI.deployTargetingFallbackUnit')
          })}
          {!isMandatoryTargeting && (
            <TargetingCancelButton onClick={onCancelDeploy}>
              {t('battleUI.cancelTargeting')}
            </TargetingCancelButton>
          )}
          {isMandatoryTargeting && (
            <span className="mandatory-targeting-note" style={{ marginLeft: '10px', color: '#ff6b6b', fontSize: '0.9em' }}>
              {t('battleUI.mandatoryDeployTargetingNote')}
            </span>
          )}
        </div>
      )}
      
      {strategemMode && (
        <div className="targeting-message">
          {getStrategemTargetingPrompt(strategemTargetInfo, {
            displayName: strategemOrderDisplayName
          })}
          {typeof document !== 'undefined' && !isTutorialCancelTargetingDisabled() && (
            <TargetingCancelButton onClick={onCancelStrategem}>
              {t('battleUI.cancelTargeting')}
            </TargetingCancelButton>
          )}
          {typeof document !== 'undefined' && isTutorialCancelTargetingDisabled() && (
            <span className="mandatory-targeting-note" style={{ marginLeft: '10px', color: '#aaa', fontSize: '0.9em' }}>
              {t('battleUI.tutorialSelectIndicatedTargetNote')}
            </span>
          )}
        </div>
      )}

      {weaponMode && (
        <div className="targeting-message">
          {typeof weaponOrderDisplayName === 'string' && weaponOrderDisplayName.trim()
            ? t('battleUI.weaponAttachTargetingBannerNamed', { name: weaponOrderDisplayName.trim() })
            : t('battleUI.weaponAttachTargetingBanner')}
          {!isTutorialCancelTargetingDisabled() && (
            <TargetingCancelButton onClick={onCancelWeapon}>
              {t('battleUI.cancelTargeting')}
            </TargetingCancelButton>
          )}
          {isTutorialCancelTargetingDisabled() && (
            <span className="mandatory-targeting-note" style={{ marginLeft: '10px', color: '#aaa', fontSize: '0.9em' }}>
              {t('battleUI.tutorialSelectIndicatedTargetNote')}
            </span>
          )}
        </div>
      )}

      {weaponDeployMode && (
        <div className="targeting-message">
          {getWeaponTargetingMessage(weaponDeployTargetInfo?.weaponId)}
          {!isTutorialCancelTargetingDisabled() && (
            <TargetingCancelButton onClick={onCancelWeaponDeploy}>
              {t('battleUI.cancelTargeting')}
            </TargetingCancelButton>
          )}
          {isTutorialCancelTargetingDisabled() && (
            <span className="mandatory-targeting-note" style={{ marginLeft: '10px', color: '#aaa', fontSize: '0.9em' }}>
              {t('battleUI.tutorialSelectIndicatedTargetNote')}
            </span>
          )}
        </div>
      )}

      {droneMode && (
        <div className="targeting-message">
          {typeof droneOrderDisplayName === 'string' && droneOrderDisplayName.trim()
            ? t('battleUI.droneTargetingBannerNamed', { name: droneOrderDisplayName.trim() })
            : t('battleUI.droneTargetingBanner')}
          <TargetingCancelButton onClick={onCancelDrone}>
            {t('battleUI.cancelTargeting')}
          </TargetingCancelButton>
        </div>
      )}

      {euEpZoneMode && (
        <div className="targeting-message">
          {t('exploreUnknownBattle.epZoneSelectZonePrompt')}
          <TargetingCancelButton onClick={onCancelEuEpZone}>
            {t('battleUI.cancelTargeting')}
          </TargetingCancelButton>
        </div>
      )}

      {euEpTargetMode && (
        <div className="targeting-message">
          {t('exploreUnknownBattle.epTargetSelectUnitPrompt')}
          <TargetingCancelButton onClick={onCancelEuEpTarget}>
            {t('battleUI.cancelTargeting')}
          </TargetingCancelButton>
        </div>
      )}
    </div>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  const portalLayout = buildPortalShellStyle(portalAnchor);

  return createPortal(
    <div className="targeting-cancel-dialog-portal-shell" style={portalLayout.shell}>
      <div className="targeting-cancel-dialog-portal-stage" style={portalLayout.stage}>
        {dialog}
      </div>
    </div>,
    document.body
  );
};

export default TargetingCancelDialog;
